import { CustomAxiosRequestConfig, http } from '@/core/helpers/axiosHelper';
import { ApiResource } from '@/core/models/ApiResource.interface';
import { BatchJob } from '@/core/models/JobBatch.interface';
import { LocationQuery } from 'vue-router';
import { Batch } from '../batches/batch.interface';
import { Customer } from '../customers/customer.interface';
import { Employment } from '../employments/employment.interface';

export const CONTRACT_INCLUDES = [
   'commuterTraffic',
   'contractAnomalies',
   'contractAnomalies.anomaly.anomalyBlockLevel',
   'contractTemplate',
   'contractTemplate.contractTemplateFields',
   'contractTemplate.defaultCommercialAgreement.performanceCode',
   'contractTemplate.employerCompanyJointCommittee',
   'contractTemplate.userCompanyJointCommittee',
   'costCenter',
   'defaultCommercialAgreement.performanceCode',
   'department',
   'dimonaStatus',
   'documents',
   'easypayContractMapping.easypayImportFile',
   'employee',
   'employee.employeeAddresses.city',
   'employerCompanyJointCommittee',
   'employmentCity',
   'employmentCity.country',
   'fees',
   'fulltime',
   'invoiceGroup',
   'jobFunction',
   'latestDimonaRequest.dimonaAnomalies',
   'optionalCommercialAgreements',
   'performances',
   'performances.performanceCode',
   'printStatus',
   'salaryInclude',
   'shift',
   'shiftType',
   'team',
   'team',
   'turnoverTeams',
   'userCompanyJointCommittee',
   'workerStatute',
   'contractProcessState',
   'easypayContractProcessState',
];

export class ContractService {
   async getCustomersWithContracts(query: LocationQuery) {
      return await http
         .get<ApiResource<Array<Customer>>>(`/contract-customers`, {
            cancelable: false,
            params: { ...query, embed: 'company.departments,company.shifts,company.costCenters,company.city' },
         } as CustomAxiosRequestConfig)
         .then((response) => response.data.data);
   }

   async getContracts(query: LocationQuery) {
      return await http
         .get(`/contracts`, {
            params: { ...query },
            cancelable: false,
         } as CustomAxiosRequestConfig)
         .then((response) => response.data);
   }

   async getContractById(contractId: number, query?: LocationQuery) {
      const params = {
         ...query,
         embed: query?.embed ?? CONTRACT_INCLUDES.join(','),
      };
      return await http.get(`/contracts/${contractId}`, { params }).then((response) => response.data);
   }

   async putContract(contractId: number, contract: Employment) {
      return await http.put(`/contracts/${contractId}`, contract).then((response) => response.data.data);
   }

   async deleteContract(contractId: number) {
      return await http.delete(`/contracts/${contractId}`).then((response) => response.data);
   }

   async getLastContractFromCustomerAndEmployeeAndDate(customerId: number, employeeId: number, date: string) {
      return await http
         .get(`/customers/${customerId}/employees/${employeeId}/contracts`, {
            params: {
               sort: '-from',
               page: 1,
               before_from: date,
               per_page: 1,
               embed: 'employee,department,fulltimeWeek,cost_center,worker_statute,shift,userCompanyJointCommittee,employerCompanyJointCommittee,job_function,fees,optionalCommercialAgreements,turnoverTeams,commuterTraffic',
            },
         })
         .then((response) => response.data);
   }

   async postPerformanceChange(painterBody: any) {
      return await http.post(`/paint-performance-change`, painterBody).then((response: any) => {
         return response.data;
      });
   }

   async postContractTranslations(importFile: any) {
      const formData = new FormData();
      formData.append('file', importFile);

      return await http.post(`/contracts-translations`, formData).then((response) => response.data);
   }

   async uploadContracts(importFile: any, translations: any) {
      const formData = new FormData();
      formData.append('file', importFile);
      Object.entries(translations).forEach(([key, value]: any) => {
         formData.append(key, value);
      });
      return await http
         .post(`/upload-contracts`, formData)
         .then((response) => {
            return response.data;
         })
         .catch((error) => {
            throw new Error(error.response.data.message);
         });
   }

   async patchContract(contractId: number, itemsToPatch: Array<any>) {
      return await http.patch(`/contracts/${contractId}`, itemsToPatch).then((response) => response.data.data);
   }

   async signContract(contractId: number) {
      return http.put(`/contracts/${contractId}/sign`).then((response) => response.data);
   }

   async indexContractCommuterTrafficBatches(contractIds: Array<number>) {
      return http
         .post(`/index-contract-commuter-traffic-batches`, {
            contract_ids: contractIds,
         })
         .then((response) => response.data);
   }

   async copyContractDataBatches(contractId: number, employment: Employment) {
      return await http.put(`/contracts/${contractId}/copy-contract-data-batches`, employment).then((response) => response.data.data);
   }

   async print(contractId: number) {
      return await http.get(`/contracts/${contractId}/print`);
   }

   async batchDeleteContracts(contract_ids: Array<number>) {
      const response = await http.delete<Batch>(`/contract-batch`, { data: { contract_ids } });
      return response.data;
   }

   async exportContracts(contract_ids: Array<number>) {
      const response = await http.post<BatchJob>(`/contract-export`, { contracts: [...contract_ids.map((id) => ({ id }))] });
      return response.data;
   }
}
